import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useCustomerDetails } from '@techstyle/react-accounts';

import { useInView } from '../../utils/useInView';
import { useProductTrackingUtils } from '../../utils/useProductTrackingUtils';
import { Component as Carousel } from '../Carousel';

if (process.browser) {
  require('intersection-observer');
}

const ProductTrackingCarousel = ({
  children,
  products = [],
  fplId = null,
  listId = null,
  ...rest
}) => {
  const carouselId = listId || fplId;
  const tracking = useSelector(state =>
    state.tracking ? state.tracking[carouselId] : {}
  );
  const [rootMargin, setRootMargin] = useState('0px 0px 0px 0px');
  const time = useSelector(state => state.time || {});
  const [profile = {}] = useCustomerDetails(['profile']);
  const router = useRouter();
  const carouselRef = useRef(null);

  useLayoutEffect(() => {
    if (products.length) {
      const { masterProductId: mpId, master_product_id: _masterProductId } =
        products[0];

      const masterProductId = _masterProductId || mpId;
      const firstSlide = document.getElementById(masterProductId);
      if (firstSlide) {
        setRootMargin(
          `0px 0px ${
            carouselRef.current.getBoundingClientRect().height -
            firstSlide.getBoundingClientRect().height
          }px 0px`
        );
      }
    }
  }, [products]);

  const {
    productCallback,
    containerCallback,
    clearTracking,
    refreshProducts,
    setObserver,
    addProductRef,
  } = useProductTrackingUtils({
    tracking,
    products,
    listId,
    fplId,
    profile,
    time,
  });

  const productObserver = useInView(productCallback, {
    root: carouselRef.current,
    threshold: [0.85],
  });

  setObserver(productObserver);

  const containerObserver = useInView(containerCallback, {
    threshold: 0.99, // Safari is trash =D pt1
    root: null, // Safari is trash =D pt2
    rootMargin,
  });

  const refreshContainerObserver = useCallback(() => {
    if (carouselRef.current instanceof Element) {
      containerObserver.unobserve(carouselRef.current);
      containerObserver.observe(carouselRef.current);
    }
  }, [containerObserver]);

  useLayoutEffect(() => {
    refreshContainerObserver();
  }, [containerObserver, rootMargin, refreshContainerObserver]);

  useEffect(() => {
    window.addEventListener('pageshow', event => {
      // This is because safari uses this thing called bfcache
      if (event.persisted) {
        clearTracking();
        if (carouselRef.current instanceof Element) {
          containerObserver.unobserve(carouselRef.current);
          containerObserver.observe(carouselRef.current);
        }
      }
    });
    // This is because safari uses this thing called bfcache
    window.addEventListener('pagehide', clearTracking);

    containerObserver.observe(carouselRef.current);
    refreshProducts();

    return () => {
      router.events.off('routeChangeStart', () => {});

      router.events.off('routeChangeComplete', () => {});
      router.events.off('routeChangeError', () => {});

      window.removeEventListener('pageshow', () => {});
      window.removeEventListener('pagehide', () => {});
    };
  }, [
    clearTracking,
    containerObserver,
    refreshProducts,
    router.events,
    refreshContainerObserver,
  ]);

  return (
    <div ref={carouselRef}>
      <Carousel {...rest}>
        {typeof children === 'function' ? children(addProductRef) : children}
      </Carousel>
    </div>
  );
};

ProductTrackingCarousel.propTypes = {
  /**
   * Class(es) to apply to the container element.
   */
  className: PropTypes.string,
  /**
   * Can be components or an array.
   */
  children: PropTypes.node,
  /**
   * Props to pass along to [react-slick](https://github.com/akiran/react-slick).
   * These will be merged with the defaults provided by this component.
   */
  options: PropTypes.object,

  tracking: PropTypes.object,

  listId: PropTypes.string.isRequired,

  fplId: PropTypes.string,

  products: PropTypes.array.isRequired,

  rootMargin: PropTypes.string,
};

ProductTrackingCarousel.NextArrow = Carousel.NextArrow;
ProductTrackingCarousel.PrevArrow = Carousel.PrevArrow;

export default ProductTrackingCarousel;
